import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle, XCircle, Mail, ArrowLeft, RefreshCw } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { Button, Card } from '../ui'
import { authService } from '../../services/authService'
import { AnalyticsEvents } from '../../types/analytics'

// Environment configuration for analytics
const ENV = import.meta.env.VITE_ENV
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const isAnalyticsEnabled = ENV === 'production' && !!POSTHOG_KEY

const EmailVerificationPage: React.FC = () => {
  const posthog = usePostHog()
  const hasTrackedVerification = useRef(false)
  const [status, setStatus] = useState<
    'loading' | 'success' | 'error' | 'already-verified'
  >('loading')
  const [error, setError] = useState<string>('')
  const [isResending, setIsResending] = useState(false)

  useEffect(() => {
    const verifyEmail = async () => {
      // Extract token from URL parameters
      const urlParams = new URLSearchParams(window.location.search)
      const tokenFromUrl = urlParams.get('token')

      if (!tokenFromUrl) {
        setError('Invalid or missing verification token')
        setStatus('error')
        return
      }

      try {
        const response = await authService.verifyEmail({ token: tokenFromUrl })

        if (response.success) {
          // Track email verification success (only once)
          if (isAnalyticsEnabled && posthog && !hasTrackedVerification.current) {
            hasTrackedVerification.current = true
            posthog.capture(AnalyticsEvents.AUTH_EMAIL_VERIFIED)
          }
          setStatus('success')

          // Update registration store to mark email as verified
          const registrationData = localStorage.getItem('registration-storage')
          if (registrationData) {
            try {
              const parsed = JSON.parse(registrationData)
              if (parsed.state?.registrationData) {
                parsed.state.registrationData.emailVerified = true
                localStorage.setItem(
                  'registration-storage',
                  JSON.stringify(parsed)
                )
              }
            } catch (e) {
              console.error('Failed to update registration data:', e)
            }
          }

          // Backend returns redirectUrl pointing to login with returnUrl parameter
          // User must log in manually to proceed to checkout
          setTimeout(() => {
            window.location.href =
              response?.redirectUrl ||
              '/?returnUrl=/onboarding/checkout&verified=true'
          }, 2000)
        } else {
          // Handle specific error cases
          if (
            response.error?.code === 'TOKEN_ALREADY_USED' ||
            response.error?.code === 'EMAIL_ALREADY_VERIFIED'
          ) {
            setStatus('already-verified')
          } else {
            setError(
              response.error?.message ||
                response.message ||
                'Email verification failed'
            )
            setStatus('error')
          }
        }
      } catch (error) {
        setError(
          error instanceof Error ? error.message : 'Email verification failed'
        )
        setStatus('error')
      }
    }

    verifyEmail()
  }, [])

  const handleBackToLogin = () => {
    // Redirect to login page
    window.location.href = '/'
  }

  const handleResendVerification = async () => {
    // This would need to be implemented if there's a resend endpoint
    setIsResending(true)
    try {
      // Assuming there's a resend verification endpoint
      // await authService.resendVerificationEmail({ email: userEmail });
      // For now, just show a message
      alert('Please contact support to resend your verification email.')
    } catch (error) {
      console.error('Failed to resend verification:', error)
    } finally {
      setIsResending(false)
    }
  }

  if (status === 'loading') {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className='text-center'
        >
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-avi-primary mx-auto mb-4'></div>
          <p className='text-gray-600'>Verifying your email address...</p>
        </motion.div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className='max-w-md w-full space-y-8'
        >
          <Card className='p-8 text-center'>
            <div className='mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100 mb-4'>
              <CheckCircle className='h-6 w-6 text-green-600' />
            </div>
            <h2 className='text-2xl font-bold text-gray-900 mb-2'>
              Email verified successfully!
            </h2>
            <p className='text-gray-600 mb-6'>
              Your email address has been verified. Redirecting you to log in to
              continue...
            </p>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-avi-primary mx-auto'></div>
          </Card>
        </motion.div>
      </div>
    )
  }

  if (status === 'already-verified') {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className='max-w-md w-full space-y-8'
        >
          <Card className='p-8 text-center'>
            <div className='mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 mb-4'>
              <Mail className='h-6 w-6 text-blue-600' />
            </div>
            <h2 className='text-2xl font-bold text-gray-900 mb-2'>
              Email already verified
            </h2>
            <p className='text-gray-600 mb-6'>
              This email address has already been verified. You can log in to
              your account.
            </p>
            <Button onClick={handleBackToLogin} className='w-full'>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Go to login
            </Button>
          </Card>
        </motion.div>
      </div>
    )
  }

  // Error state
  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className='max-w-md w-full space-y-8'
      >
        <Card className='p-8 text-center'>
          <div className='mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100 mb-4'>
            <XCircle className='h-6 w-6 text-red-600' />
          </div>
          <h2 className='text-2xl font-bold text-gray-900 mb-2'>
            Verification failed
          </h2>
          <p className='text-gray-600 mb-6'>
            {error ||
              'This verification link is invalid, expired, or has already been used.'}
          </p>
          <div className='space-y-3'>
            <Button
              onClick={handleResendVerification}
              variant='outline'
              className='w-full'
              disabled={isResending}
            >
              {isResending ? (
                <>
                  <RefreshCw className='h-4 w-4 mr-2 animate-spin' />
                  Resending...
                </>
              ) : (
                <>
                  <RefreshCw className='h-4 w-4 mr-2' />
                  Resend verification email
                </>
              )}
            </Button>
            <Button
              onClick={handleBackToLogin}
              variant='outline'
              className='w-full'
            >
              <ArrowLeft className='h-4 w-4 mr-2' />
              Back to login
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  )
}

export default EmailVerificationPage
